<script setup lang="ts">
/**
 * Компонент header приложения
 * Используется в MainLayout для уменьшения дублирования кода
 */
interface Props {
  showUserMenu?: boolean;
  showBurgerMenu?: boolean;
  reservedRight?: number;
  /** Класс ширины содержимого (`max-w-*`): шапка остаётся на всю ширину, содержимое встаёт по оси колонки. */
  contentClass?: string;
}

withDefaults(defineProps<Props>(), {
  showUserMenu: false,
  showBurgerMenu: false,
  reservedRight: 0,
  contentClass: '',
});

const emit = defineEmits<{
  burgerClick: [];
}>();

const handleBurgerClick = () => {
  emit('burgerClick');
};
</script>

<script lang="ts">
import { UserMenu } from '@/features/session';
import { IconMenu } from '@/shared/ui';

import LocationPlaque from './LocationPlaque.vue';
</script>

<template>
  <header
    class="app-header"
    :class="{ 'app-header--flat': showBurgerMenu }"
    data-pull-to-refresh-ignore
  >
    <div :class="['mx-auto flex w-full min-w-0 items-center justify-between', contentClass]">
      <div class="app-header__left min-w-0 flex-1">
        <!-- Плашка локации на мобильном: рейла с ней здесь нет. -->
        <LocationPlaque
          v-if="showBurgerMenu"
          variant="header"
        />

        <!-- Слот для логотипа или заголовка страницы -->
        <slot name="logo">
          <slot name="page-title">
            <slot name="navigation" />
          </slot>
        </slot>
      </div>

      <div
        class="app-header__right"
        :style="{ marginRight: `${reservedRight}px` }"
      >
        <!-- Слот для дополнительных действий -->
        <slot name="header-actions" />

        <!-- User Menu -->
        <UserMenu v-if="showUserMenu" />

        <!-- Кнопка-бургер для mobile -->
        <button
          v-if="showBurgerMenu"
          class="app-header__burger"
          aria-label="Меню"
          @click="handleBurgerClick"
        >
          <IconMenu />
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  height: var(--header-height);
  border-radius: 0.75rem;
  background: var(--surface-card);
  padding: 0 1rem;
}

.app-header__left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.app-header__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: margin-right 180ms ease;
}

.app-header__burger {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0.5rem;
  color: var(--text-primary);
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    transform 0.15s ease;
}

.app-header__burger:hover {
  background: var(--surface-hover);
}

.app-header__burger:active {
  transform: scale(0.96);
}

/* sm: 640px+ — возвращаем десктопные паддинги/gap */
@media (min-width: 640px) {
  .app-header {
    padding: 0 1.5rem;
  }

  .app-header__left {
    gap: 1.5rem;
  }

  .app-header__right {
    gap: 1rem;
  }
}

/* Телефон и планшет: шапка на всю ширину экрана, поверх отступов .main-content.
   max-width держит меню на экране на страницах с min-width шире него. */
.app-header--flat {
  height: calc(3.5rem + env(safe-area-inset-top, 0px));
  max-width: 100dvw;
  margin-top: calc(-1 * var(--main-inset-top));
  margin-right: calc(-1 * var(--main-inset-right));
  margin-left: calc(-1 * var(--main-inset-left));
  padding: env(safe-area-inset-top, 0px) max(0.25rem, env(safe-area-inset-right, 0px)) 0
    max(0.75rem, env(safe-area-inset-left, 0px));
  border-radius: 0;
}

/* Показываем burger только на mobile */
@media (min-width: 1280px) {
  .app-header__burger {
    display: none;
  }
}
</style>
